import { SectionSchedule } from '../../../types/sectionMaintainerTypes';
import { Currency } from '../../../types/tariff';

/**
 * Tipo de programa de educación continua (del NonStructuredStudyPlan)
 */
export enum NonStructuredType {
  COURSE = 'course',
  PROGRAM_COURSE = 'program_course',
}

/**
 * Estado de la oferta de educación continua
 */
export enum NonStructuredAcademicOfferStatus {
  Borrador = 'Borrador',
  Activa = 'Activa',
  Cerrada = 'Cerrada',
}

/**
 * Interfaz para TuitionProcessType
 */
export interface TuitionProcessType {
  id: number;
  name: string;
  code: string;
}

export type AcademicOfferRequirement = {
  ceAdmissionRequirementId: string;
  description: string;
  requiresDocument: boolean;
};

export type NonStructuredAcademicOfferAdmissionRequirement = {
  id: number;
  ceAcademicOfferId: number;
  ceAdmissionRequirementId: string;
  description: string;
  requiresDocument: boolean;
  isActive: boolean;
  ceAdmissionRequirement: {
    id: string;
    code: string;
    description: string;
    requiresDocument: boolean;
  };
};

export type AcademicOfferFinantialConfig = {
  code: string;
  value: number;
};

/**
 * Interfaz para TuitionProcess
 */
export interface TuitionProcess {
  id: number;
  name: string;
  tuitionProcessType?: TuitionProcessType;
}

/**
 * Interfaz para TuitionSubprocess
 */
export interface TuitionSubprocess {
  id: number;
  type: string;
  process?: TuitionProcess;
}

/**
 * Interfaz para StudyPlanVersionOfferPeriodAdmission
 */
export interface StudyPlanVersionOfferPeriodAdmission {
  id: number;
  quota: number | null;
  subprocess?: TuitionSubprocess;
}

export interface SectionPackages {
  id: number;
  sections: AcademicOfferScheduleSection[];
}

/**
 * Interfaz para Modality
 */
export interface Modality {
  id: number;
  name: string;
  code: string;
  deliveryMode: string;
}

/**
 * Interfaz para StudyPlan (plan de estudio de EC)
 */
export interface StudyPlan {
  id: number;
  name: string;
  code: string;
  school?: { id: number; name: string };
  modality?: Modality;
  academicLevel?: { id: number; name: string };
  academicLevelId?: number;
  studyPlanCe?: {
    id: number;
    type: NonStructuredType;
  };
}

/**
 * Interfaz para StudyPlanVersionOffer
 */
export interface StudyPlanVersionOffer {
  id: number;
  campusId: number;
  scheduleId: number;
  campus?: {
    id: number;
    name: string;
  };
  schedule?: {
    id: number;
    name: string;
  };
  studyPlanVersion?: {
    id: number;
    name: string;
    studyPlan?: StudyPlan;
  };
}

/**
 * Interfaz para AcademicOfferStudyPlanVersionOffer (relación intermedia)
 */
export interface OfferQuotaHistory {
  quota: number;
  createdAt: string;
  accountFullName: string;
}

export interface AcademicOfferStudyPlanVersionOffer {
  id: number;
  quota: number | null;
  completionDaysLimit: number | null;
  duration: string | null;
  startDate?: string | null;
  endDate?: string | null;
  studyPlanVersionOffer?: StudyPlanVersionOffer;
  admissions?: StudyPlanVersionOfferPeriodAdmission[];
  sectionPackages?: SectionPackages[];
  offerQuotaHistories?: OfferQuotaHistory[];
  enrolled?: number;
  enrolledCount?: number;
}

/**
 * Interfaz para la oferta de educación continua (NonStructuredAcademicOffer)
 */
export interface NonStructuredAcademicOffer {
  id: number;
  status: NonStructuredAcademicOfferStatus;
  firstInstallmentRuleId: number | null;
  createdAt: string;
  updatedAt: string;
  enrolled?: number;
  enrollmentStartDate?: string | null;
  enrollmentEndDate?: string | null;
  year?: number;
  offerLink?: string;
  noEnrollmentRequired?: boolean;
  tuitionProcessTypeId?: number;
  academicOfferStudyPlanVersionOffers?: AcademicOfferStudyPlanVersionOffer[];
  ceAcademicOfferAdmissionRequirements?: NonStructuredAcademicOfferAdmissionRequirement[];
  requirements?: AcademicOfferRequirement[];
  finantialConfigs?: AcademicOfferFinantialConfig[];
}

/**
 * Datos del formulario para crear/editar oferta
 */
export interface NonStructuredAcademicOfferFormData {
  campuses: Array<{ id: number; name: string }>;
  schools: Array<{ id: number; name: string }>;
  studyPlans: Array<StudyPlan>;
  modalities: Array<Modality>;
  academicLevels: Array<{ id: number; name: string }>;
  admissionRequirements: Array<{ id: string; name: string }>;
  processTypes: Array<TuitionProcessType>;
  defaultCurrency: Currency;
}

/**
 * Filtros para la búsqueda de ofertas
 */
export interface NonStructuredAcademicOfferFilters {
  processTypeId?: number;
  programType?: NonStructuredType;
  studyPlanId?: number;
  modalityId?: number;
  academicLevelId?: number;
  campusId?: number;
  schoolId?: number;
  status?: NonStructuredAcademicOfferStatus;
  page?: number;
  items_per_page?: number;
}

/**
 * Respuesta paginada de ofertas
 */
export interface NonStructuredAcademicOfferPaginatedResponse {
  data: NonStructuredAcademicOffer[];
  total: number;
  total_pages: number;
}

/**
 * Payload para crear una oferta de educación continua
 */
export interface CreateNonStructuredAcademicOfferPayload {
  studyPlanId: number;
  campusId: number;
  quota?: number;
  completionDaysLimit?: number;
  requirements?: AcademicOfferRequirement[];
  finantials?: AcademicOfferFinantialConfig[];
}

/**
 * Payload para actualizar una oferta de educación continua
 */
export interface UpdateNonStructuredAcademicOfferPayload {
  firstInstallmentRuleId?: number | null;
  quota?: number;
  completionDaysLimit?: number | null;
  requirements?: AcademicOfferRequirement[];
  finantials?: AcademicOfferFinantialConfig[];
}

/**
 * Enum de pasos para la creación de oferta académica de EC
 */
export enum NonStructuredAcademicOfferStepEnum {
  Offer = 'offer',
  Sections = 'sections',
  Schedule = 'schedule',
  Summary = 'summary',
}

/**
 * Respuesta del endpoint de paso actual
 */
export interface NonStructuredAcademicOfferStepResponse {
  id: number;
  ceAcademicOfferId: number;
  currentStep: NonStructuredAcademicOfferStepEnum;
  createdAt: string;
  updatedAt: string;
}

/**
 * Datos de sección reducidos para el calendario agregado de una oferta de EC.
 * Se usa en el paso 3 (Horario) del wizard para mostrar los horarios
 * de todas las secciones de la oferta en un solo calendario.
 */
export interface AcademicOfferScheduleSection {
  id: number;
  name: string;
  course: { id: number; name: string };
  sectionSchedules: SectionSchedule[];
}

/**
 * Respuesta del endpoint de horarios agregados de una oferta CE.
 */
export interface NonStructuredAcademicOfferScheduleResponse {
  modalityDeliveryMode: string | null;
  sections: AcademicOfferScheduleSection[];
  timeModules: {
    id: number;
    name: string;
    shortening: string;
    startTime: string;
    endTime: string;
  }[];
  activities: { id: number; name: string }[];
}

/**
 * Fila de la tabla de secciones en el resumen
 */
export interface SummarySectionRow {
  subjectName: string;
  sectionName: string;
  modality: string;
}

/**
 * Respuesta del endpoint de resumen de una oferta CE.
 */
export interface NonStructuredAcademicOfferSummaryResponse {
  offer: NonStructuredAcademicOffer;
  sections: SummarySectionRow[];
  ceProcesses: TuitionProcess[];
}

/**
 * Payload para publicar una oferta de educación continua
 */
export interface PublishNonStructuredAcademicOfferPayload {
  tuitionProcessId: number;
  year: number;
  classStartDate?: string;
  classEndDate?: string;
  enrollmentStartDate?: string;
  enrollmentEndDate?: string;
  offerLink?: string;
  noEnrollmentRequired?: boolean;
  completionDaysLimit?: number;
}

/**
 * Payload para actualizar datos de resumen de una oferta activa
 */
export interface UpdateNonStructuredAcademicOfferSummaryPayload {
  year: number;
  classStartDate?: string;
  classEndDate?: string;
  enrollmentStartDate?: string;
  enrollmentEndDate?: string;
  offerLink?: string;
  noEnrollmentRequired?: boolean;
  completionDaysLimit?: number;
}

/**
 * Tipo genérico para opciones de Select
 */
export interface SelectOptionType {
  label: string;
  value: number;
  data?: any;
}
